import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/atoms';
import { cn } from '@/lib/utils';
import { useVoiceRecorder } from '@/hooks/use-voice-recorder';
import {
  CHAT_IMAGE_ACCEPT,
  CHAT_MAX_IMAGE_BYTES,
  CHAT_MAX_VOICE_BYTES,
} from '@/lib/constants';
import type { ChatOutboundPayload } from '@/types';
import {
  ImageIcon,
  MicIcon,
  PaperclipIcon,
  PauseIcon,
  PlusIcon,
  SendIcon,
  SmileIcon,
  SquareIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';

const ChatEmojiPicker = lazy(() =>
  import('@/components/molecules/chat-emoji-picker').then((m) => ({
    default: m.ChatEmojiPicker,
  }))
);

export interface ChatComposerProps {
  onSend: (payload: ChatOutboundPayload) => void;
  disabled?: boolean;
  /** Disables free-form text/media while keeping templates available. */
  freeFormDisabled?: boolean;
  showTemplates?: boolean;
  onOpenTemplates?: () => void;
  /** When `key` changes, replaces the draft text (e.g. AI suggestion). */
  prefill?: { text: string; key: number } | null;
  className?: string;
}

type PendingImage = {
  id: string;
  file: File;
  previewUrl: string;
};

function validateFile(file: File, maxBytes: number, label: string): boolean {
  if (file.size > maxBytes) {
    toast.error(
      `${label} must be under ${Math.round(maxBytes / (1024 * 1024))}MB.`
    );
    return false;
  }
  return true;
}

export function ChatComposer({
  onSend,
  disabled,
  freeFormDisabled = false,
  showTemplates = false,
  onOpenTemplates,
  prefill,
  className,
}: ChatComposerProps) {
  const [text, setText] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);

  useEffect(() => {
    if (prefill?.text) setText(prefill.text);
  }, [prefill?.key, prefill?.text]);

  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuView, setMobileMenuView] = useState<'actions' | 'emoji'>(
    'actions'
  );

  const imageInputRef = useRef<HTMLInputElement>(null);
  const pendingImagesRef = useRef(pendingImages);
  pendingImagesRef.current = pendingImages;

  const voice = useVoiceRecorder();

  const voicePreviewUrl = useMemo(() => {
    if (voice.state !== 'stopped') return null;
    const blob = voice.getBlob();
    return blob ? URL.createObjectURL(blob) : null;
  }, [voice.state, voice.durationMs, voice.getBlob]);

  useEffect(() => {
    return () => {
      if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
    };
  }, [voicePreviewUrl]);

  useEffect(() => {
    return () => {
      pendingImagesRef.current.forEach((image) => {
        URL.revokeObjectURL(image.previewUrl);
      });
    };
  }, []);

  const insertEmoji = (emoji: string) => {
    setText((prev) => prev + emoji);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileMenuView('actions');
  };

  const openMobileEmoji = () => {
    setMobileMenuView('emoji');
  };

  const handleMobileEmojiSelect = (emoji: string) => {
    insertEmoji(emoji);
    closeMobileMenu();
  };

  const handleMobileImagePick = () => {
    closeMobileMenu();
    imageInputRef.current?.click();
  };

  const handleMobileVoiceStart = () => {
    closeMobileMenu();
    void voice.start();
  };

  const composerLocked = disabled || freeFormDisabled;

  const showMobilePlus =
    !voice.isActive && voice.state !== 'stopped' && !composerLocked;

  const sendText = () => {
    const trimmed = text.trim();
    if (!trimmed || composerLocked) return;
    onSend({ kind: 'text', text: trimmed });
    setText('');
  };

  const clearPendingImages = () => {
    setPendingImages((current) => {
      current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      return [];
    });
  };

  const removePendingImage = (id: string) => {
    setPendingImages((current) => {
      const target = current.find((image) => image.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return current.filter((image) => image.id !== id);
    });
  };

  const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    setAttachOpen(false);
    if (!files.length || composerLocked) return;

    const valid = files.filter((file) =>
      validateFile(file, CHAT_MAX_IMAGE_BYTES, 'Images')
    );
    if (!valid.length) return;

    setPendingImages((current) => [
      ...current,
      ...valid.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    ]);
  };

  const sendPendingImages = () => {
    if (!pendingImages.length || composerLocked) return;
    onSend({
      kind: 'media',
      files: pendingImages.map((image) => image.file),
      mediaType: 'image',
      caption: text.trim() || undefined,
    });
    setText('');
    clearPendingImages();
  };

  const sendVoice = () => {
    const file = voice.getFile();
    if (!file || composerLocked) return;
    if (!validateFile(file, CHAT_MAX_VOICE_BYTES, 'Voice notes')) {
      voice.reset();
      return;
    }
    onSend({ kind: 'media', files: [file], mediaType: 'voice' });
    voice.reset();
  };

  const formatVoiceDuration = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  };

  const hasPendingImages = pendingImages.length > 0;
  const canSendText = !!text.trim() && !composerLocked;
  const canSendPendingImages = hasPendingImages && !composerLocked;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {hasPendingImages && (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-gray-900">
              Image preview ({pendingImages.length})
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-muted-foreground"
              onClick={clearPendingImages}
            >
              Clear all
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {pendingImages.map((image) => (
              <div
                key={image.id}
                className="relative size-20 overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
              >
                <img
                  src={image.previewUrl}
                  alt={image.file.name}
                  className="size-full object-cover"
                />
                <button
                  type="button"
                  className="absolute right-1 top-1 flex size-6 items-center justify-center rounded-full bg-black/60 text-white"
                  onClick={() => removePendingImage(image.id)}
                  aria-label={`Remove ${image.file.name}`}
                >
                  <XIcon className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearPendingImages}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={sendPendingImages}>
              Send {pendingImages.length > 1 ? 'photos' : 'photo'}
            </Button>
          </div>
        </div>
      )}

      {voice.isActive && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-red-500" />
            </span>
            Recording {formatVoiceDuration(voice.durationMs)}
          </div>
          <div className="flex items-center gap-1">
            {voice.state === 'recording' ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={voice.pause}
              >
                <PauseIcon className="size-4" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={voice.resume}
              >
                <MicIcon className="size-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={voice.stop}
            >
              <SquareIcon className="size-4 fill-current" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-destructive"
              onClick={voice.reset}
            >
              <Trash2Icon className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {voice.state === 'stopped' && (
        <div className="rounded-lg border bg-white px-3 py-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-900">
              Voice note preview
            </span>
            <span className="text-xs text-muted-foreground">
              {formatVoiceDuration(voice.durationMs)}
            </span>
          </div>
          {voicePreviewUrl && (
            <audio
              src={voicePreviewUrl}
              controls
              className="mb-3 w-full"
              preload="metadata"
            />
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={voice.reset}
            >
              Delete
            </Button>
            <Button type="button" size="sm" onClick={sendVoice}>
              Send voice note
            </Button>
          </div>
        </div>
      )}

      {voice.error && <p className="text-xs text-destructive">{voice.error}</p>}

      <div className="flex items-center gap-1.5 sm:gap-2">
        <div className="flex w-full min-w-0 items-center gap-1 rounded-lg bg-white p-2 lg:border lg:border-gray-200">
          {showMobilePlus && (
            <Popover
              open={mobileMenuOpen}
              onOpenChange={(open) => {
                setMobileMenuOpen(open);
                if (!open) setMobileMenuView('actions');
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="size-9 shrink-0 rounded-full p-0 lg:hidden"
                  aria-label="More message options"
                >
                  <PlusIcon className="size-5 text-gray-500" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="top"
                sideOffset={8}
                className={cn(
                  'border-gray-200 p-2 shadow-lg lg:hidden',
                  mobileMenuView === 'emoji'
                    ? 'w-[min(100vw-1.5rem,352px)] overflow-hidden p-0'
                    : 'w-auto'
                )}
              >
                {mobileMenuView === 'actions' ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="flex size-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
                      onClick={openMobileEmoji}
                      aria-label="Insert emoji"
                    >
                      <SmileIcon className="size-5" />
                    </button>
                    <button
                      type="button"
                      className="flex size-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
                      onClick={handleMobileImagePick}
                      aria-label="Attach image"
                    >
                      <PaperclipIcon className="size-5" />
                    </button>
                    <button
                      type="button"
                      className="flex size-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
                      onClick={handleMobileVoiceStart}
                      aria-label="Record voice note"
                    >
                      <MicIcon className="size-5" />
                    </button>
                  </div>
                ) : (
                  <Suspense
                    fallback={
                      <div
                        className="h-[380px] animate-pulse rounded-lg bg-gray-100"
                        aria-hidden
                      />
                    }
                  >
                    <ChatEmojiPicker onEmojiSelect={handleMobileEmojiSelect} />
                  </Suspense>
                )}
              </PopoverContent>
            </Popover>
          )}

          <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="hidden size-9 shrink-0 rounded-full p-0 lg:inline-flex"
                disabled={composerLocked}
                aria-label="Insert emoji"
              >
                <SmileIcon className="size-5 text-gray-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="top"
              className="w-[min(100vw-1.5rem,352px)] overflow-hidden border-gray-200 p-0 shadow-lg"
            >
              {emojiOpen && (
                <Suspense
                  fallback={
                    <div
                      className="h-[380px] animate-pulse rounded-lg bg-gray-100"
                      aria-hidden
                    />
                  }
                >
                  <ChatEmojiPicker onEmojiSelect={insertEmoji} />
                </Suspense>
              )}
            </PopoverContent>
          </Popover>

          <Popover open={attachOpen} onOpenChange={setAttachOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="hidden size-9 shrink-0 rounded-full p-0 lg:inline-flex"
                disabled={composerLocked}
                aria-label="Attach image"
              >
                <PaperclipIcon className="size-5 text-gray-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top" className="w-44 p-1">
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-gray-100"
                onClick={() => imageInputRef.current?.click()}
              >
                <ImageIcon className="size-4" />
                Image
              </button>
            </PopoverContent>
          </Popover>

          <input
            ref={imageInputRef}
            type="file"
            accept={CHAT_IMAGE_ACCEPT}
            multiple
            className="hidden"
            onChange={handleImagePick}
          />

          {!voice.isActive && voice.state !== 'stopped' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="hidden size-9 shrink-0 rounded-full p-0 lg:inline-flex"
              disabled={composerLocked}
              aria-label="Record voice note"
              onClick={() => void voice.start()}
            >
              <MicIcon className="size-5 text-gray-500" />
            </Button>
          )}

          <Input
            placeholder={
              freeFormDisabled && showTemplates
                ? 'Use a template to message outside the 24h window'
                : freeFormDisabled
                  ? 'Response window expired...'
                  : hasPendingImages
                    ? 'Add a caption (optional)…'
                    : 'Type your message here...'
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={composerLocked}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (hasPendingImages) {
                  sendPendingImages();
                } else {
                  sendText();
                }
              }
            }}
            className="min-w-0 flex-1 border-none shadow-none ring-0 focus:border-none focus:ring-0"
          />
        </div>

        {showTemplates && (
          <Button
            type="button"
            variant={freeFormDisabled ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'h-10 shrink-0 rounded-full px-3 text-xs font-semibold sm:px-4 sm:text-sm',
              freeFormDisabled
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            )}
            disabled={disabled}
            onClick={onOpenTemplates}
          >
            Templates
          </Button>
        )}

        <Button
          size="sm"
          type="button"
          className="size-10 shrink-0 rounded-full bg-green-500 p-0 text-white hover:bg-green-600"
          disabled={composerLocked || (!canSendText && !canSendPendingImages)}
          onClick={hasPendingImages ? sendPendingImages : sendText}
          aria-label={hasPendingImages ? 'Send photos' : 'Send message'}
        >
          <SendIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}
